import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart3, Loader2, Sparkles } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const COLORS = ["#2563EB", "#14B8A6", "#6366F1", "#F59E0B", "#EC4899", "#0EA5E9"];

export default function Budget() {
  const { t, lang } = useI18n();
  const [budget, setBudget] = useState("400");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const build = async () => {
    const b = parseFloat(budget);
    if (!b || b <= 0) { toast.error(t("budget_enter_amount")); return; }
    setLoading(true);
    try { const r = await api.post("/budget", { monthly_budget: b, language: lang }); setPlan(r.data); }
    catch { toast.error(t("budget_error")); } finally { setLoading(false); }
  };

  const total = plan ? plan.allocations.reduce((s, a) => s + a.amount, 0) : 0;

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><BarChart3 className="h-7 w-7 text-brandblue" /> {t("budget_builder")}</h1>
      <p className="mt-1 text-slate-500">{t("budget_sub")}</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <label className="text-sm font-medium text-navy">{t("monthly_budget")}</label>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-2xl font-semibold text-slate-400">$</span>
            <Input data-testid="budget-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="text-2xl font-semibold h-14 rounded-xl" />
          </div>
          <Button data-testid="build-budget-btn" onClick={build} disabled={loading} className="mt-4 w-full rounded-full brand-gradient text-white py-6">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("building")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("build_my_budget")}</>}
          </Button>
        </div>

        <div className="lg:col-span-2">
          {plan ? (
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("your_monthly_budget")}</h2>
                <span className="text-sm text-slate-400">{t("total")} {money(total)}/mo</span>
              </div>
              <div className="mt-4 space-y-3">
                {plan.allocations.map((a, i) => (
                  <div key={i} data-testid={`alloc-${a.category}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="font-medium text-navy">{a.category}</span><span className="text-slate-500">{money(a.amount)}/mo</span></div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${total ? (a.amount / total) * 100 : 0}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                ))}
              </div>
              {plan.note && <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap">{plan.note}</div>}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-10 text-center text-slate-400 h-full flex items-center justify-center">{t("enter_budget")}</div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
